/* eslint-disable quotes */
import styled from "@emotion/styled";
import React, { useEffect, useState } from "react";

import Link from "../../components/Link";
import Modal from "../../components/Modal";
import Button from "../../components/Button";
import QRCode from "react-qr-code";
import StoreHeader from "../../components/StoreHeader";

type Props = {
  storeName: string;
  amount: number;
  storeLogo: string;
  orderRef: string;
  oneLinkOverPlusLimitCannotRetry: string;
  oneLinkOverPlusLimitCanRetry: string;
  oneLinkCheckoutExceededNlpLimitRetriable: string;
  oneLinkCheckoutExceededNlpLimit: string;
  oneLinkCheckoutOverNlpLimitCanRetry1x: string;
  oneLinkCheckoutOverNlpLimitCannotRetry1x: string;
  oneLinkMerchantRequiresKyc: string;
};

const StyledModal = styled(Modal)`
  header {
    background: #f6f1f1;
    ${(props) => !!props.headerContent && "flex-basis: 10rem;"}
    padding-left: 1.5rem;
    padding-right: 1.5rem;
  }

  main {
    display: flex;
    flex-flow: column nowrap;
    justify-content: space-between;
    overflow: auto;
  }

  footer {
    align-items: center;
    justify-content: center;
  }

  ul {
    padding-inline-start: 20px;
    li {
      margin-bottom: 10px;
    }
  }
`;

const Logo = styled.img`
  height: 24px;
`;

const StyledButton = styled(Button)`
  margin: 20px 0;
  @media screen and (min-width: 375px) {
    max-width: 20rem;
  }
`;

const StyledLink = styled(Link)`
  font-size: 0.75rem;
`;

const MainContent1 = styled.div`
  padding-top: 1.5rem;
`;

const MainContent2 = styled.div`
  text-align: center;
`;

const TitleText = styled.h1`
  font-size: 1.25rem;
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 1.5rem;
`;

const Centered = styled.div`
  display: flex;
  justify-content: center;
`;

const CenteredCol = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Reminder = styled.div`
  display: flex;
  align-items: center;
  background-color: #f0f0f0;
  border-radius: 1rem;
  padding: 1rem;
  gap: 1rem;
`;

const Download = styled.div`
  display: flex;
  flex-direction: column;
  background: #f6f1f1;
  border-radius: 16px;
  margin: 8px;
  padding: 16px;
  align-items: center;
`;

const QRCodeContainer = styled.div`
  padding: 8px;
  border-radius: 8px;
  background: white;
`;

const Error = (props: Partial<Props>) => {
  const [width, setWidth] = useState<number>();

  function handleWindowSizeChange() {
    setWidth(window.innerWidth);
  }
  useEffect(() => {
    handleWindowSizeChange();
    window?.addEventListener("resize", handleWindowSizeChange);
    return () => {
      window?.removeEventListener("resize", handleWindowSizeChange);
    };
  }, []);

  const isMobile = width <= 768;

  const [errorType, setErrorType] = useState("");
  const [reason, setReason] = useState("");

  const onRedirect = () => {
    window.location.href = "/redirect";
  };

  useEffect(() => {
    setErrorType(new URLSearchParams(window.location.search).get("cause") || "request");
    setReason(new URLSearchParams(window.location.search).get("reason") || "");
  }, []);

  const exceedLimitContent = {
    "exceeded-plus-limit-retriable": {
      title: "大変申し訳ございませんが、ペイディの決済を承認することができませんでした。",
      description: `ペイディのご利用可能枠を超過しているため、決済を承認することができませんでした。<br /><br />お手数ですが、下記のボタンをクリックして、店舗が提供する他の決済方法を選択してください。<br /><br />なお、ペイディアプリから「すぐ払い」機能を利用して、コンビニでご請求金額をお支払いいただくことで、ご利用いただけるようになる場合がございます。詳細なご利用方法は、<a href="https://cs-support.paidy.com/support/solutions/articles/150000040753" target="_blank" rel="noopener noreferrer">こちら</a>をご確認ください。`,
      qrcode: props.oneLinkOverPlusLimitCanRetry,
    },
    "exceeded-plus-limit": {
      title: "大変申し訳ございませんが、ペイディの決済を承認することができませんでした。",
      description: isMobile
        ? "ペイディのご利用可能枠を超過しているため、決済が承認されませんでした。<br /><br />なお、今月のご利用可能枠は、ペイディアプリよりご確認いただけます。"
        : "ペイディのご利用可能枠を超過しているため、決済を承認することができませんでした。<br /><br />なお、ご利用可能枠は、ペイディアプリよりご確認いただけます。",
      qrcode: props.oneLinkOverPlusLimitCannotRetry,
      footerContent: (
        <>
          <StyledButton fluid onClick={onRedirect}>
            他の決済方法を選ぶ
          </StyledButton>
          {isMobile ? (
            <StyledLink target="_blank" rel="noreferrer" href={props.oneLinkOverPlusLimitCannotRetry}>
              アプリを開く
            </StyledLink>
          ) : null}
        </>
      ),
    },
    "exceeded-nlp-limit-retriable": {
      title: "大変申し訳ございませんが、ペイディの決済を承認することができませんでした。",
      description: `ペイディの今月のご利用可能枠を超過しているため、決済を承認することができませんでした。<br /><br />なお、ペイディアプリから下記の機能を利用することで、ご利用いただけるようになる場合がございます。<br/><ul><li>今月のご利用分を3・6・12回あと払いに変更する</li><li>すぐ払い機能でご請求金額を支払う</li></ul><br />詳細なご利用方法は、<a target="_blank" rel="noopener noreferrer" href="https://cs-support.paidy.com/support/solutions/articles/150000040638-3%E3%83%BB6%E3%83%BB12%E5%9B%9E%E3%81%82%E3%81%A8%E6%89%95%E3%81%84%E3%81%AE%E5%88%A9%E7%94%A8%E6%96%B9%E6%B3%95%E3%81%8C%E7%9F%A5%E3%82%8A%E3%81%9F%E3%81%84">こちら</a>をご確認ください。`,
      qrcode: props.oneLinkCheckoutExceededNlpLimitRetriable,
    },
    "exceeded-nlp-limit": {
      title: "大変申し訳ございませんが、ペイディの決済を承認することができませんでした。",
      description:
        "ペイディのご利用可能枠を超過しているため、決済を承認することができませんでした。<br /><br />なお、ご利用可能枠は、ペイディアプリよりご確認いただけます。",
      qrcode: props.oneLinkCheckoutExceededNlpLimit,
    },
    "exceeded-nlp-limit-retriable-x1": {
      title: "ペイディの決済が承認されませんでした",
      description: `ペイディアプリから<a href="${props.oneLinkCheckoutOverNlpLimitCanRetry1x}" target="_blank" rel="noopener noreferrer">すぐ払い</a>機能でご請求金額をお支払いいただくと、ご利用いただけるようになる場合があります。`,
      qrcode: props.oneLinkCheckoutOverNlpLimitCanRetry1x,
    },
    "exceeded-nlp-limit-x1": {
      title: "ペイディの決済が承認されませんでした",
      description:
        "ペイディの今月のご利用可能枠を超過しています。<br /><br />今月のご利用可能枠は、ペイディアプリでご確認いただけます。",
      qrcode: props.oneLinkCheckoutOverNlpLimitCannotRetry1x,
    },
  };

  return (
    <StyledModal
      navigatorContent={<Logo src="/assets/paidy.png" alt="Paidy logo" width={61} height={24} />}
      headerContent={
        (errorType === "payment-authentication-failed" ||
        errorType === "exceeded-nlp-limit-x1" ||
        errorType === "token-authentication-expired" ||
        errorType === "forbidden" ||
        errorType === "generic") ? (
            <StoreHeader storeName={props.storeName} amount={props.amount} storeLogo={props.storeLogo} />
          ) : null
      }
      footerContent={
        errorType === "overdue" ? (
          <>
            <StyledButton fluid onClick={onRedirect}>
              今すぐ支払う
            </StyledButton>
            <StyledLink onClick={onRedirect} href="#">
              他の決済方法を選ぶ
            </StyledLink>
          </>
        ) : errorType === "exceeded-plus-limit" ? (
          <>
            <StyledButton fluid onClick={onRedirect}>
              他の決済方法を選ぶ
            </StyledButton>
            {isMobile ? (
              <StyledLink target="_blank" rel="noreferrer" href={props.oneLinkOverPlusLimitCannotRetry}>
                アプリを開く
              </StyledLink>
            ) : null}
          </>
        ) : errorType === "exceeded-plus-limit-retriable" ? (
          <>
            {isMobile ? (
              <a style={{ width: "100%" }} target="_blank" rel="noreferrer" href={props.oneLinkOverPlusLimitCanRetry}>
                <StyledButton style={{ background: "#333" }} fluid>
                  アプリを開いてコンビニで支払う
                </StyledButton>
              </a>
            ) : null}
          </>
        ) : errorType === "merchant-requires-kyc" ? (
          <>
            {isMobile ? (
              <>
                <a style={{ width: "100%" }} target="_blank" rel="noreferrer" href={props.oneLinkMerchantRequiresKyc}>
                  <StyledButton fluid>アプリから本人確認する</StyledButton>
                </a>
                <StyledLink onClick={onRedirect}>他の決済方法を選ぶ</StyledLink>
              </>
            ) : (
              <StyledButton fluid onClick={onRedirect}>
                他の決済方法を選ぶ
              </StyledButton>
            )}
          </>
        ) : errorType === "exceeded-nlp-limit-retriable" ? (
          <>
            {isMobile ? (
              <a
                style={{ width: "100%" }}
                target="_blank"
                rel="noreferrer"
                href={props.oneLinkCheckoutExceededNlpLimitRetriable}
              >
                <StyledButton style={{ background: "#333" }} fluid>
                  アプリを開く
                </StyledButton>
              </a>
            ) : (
              <StyledButton fluid onClick={onRedirect}>
                他の決済方法を選ぶ
              </StyledButton>
            )}
          </>
        ) : errorType === "exceeded-nlp-limit" ? (
          <>
            {isMobile ? (
              <>
                <StyledButton fluid onClick={onRedirect}>
                  他の決済方法を選ぶ
                </StyledButton>
                <StyledLink target="_blank" rel="noreferrer" href={props.oneLinkCheckoutExceededNlpLimit}>
                  アプリを開く
                </StyledLink>
              </>
            ) : (
              <StyledButton fluid onClick={onRedirect}>
                他の決済方法を選ぶ
              </StyledButton>
            )}
          </>
        ) : errorType === "exceeded-nlp-limit-retriable-x1" ? (
          <>
            {isMobile ? (
              <>
                <a style={{ width: "100%" }} target="_blank" href={props.oneLinkCheckoutOverNlpLimitCanRetry1x}>
                  <StyledButton fluid>すぐ払いで支払う</StyledButton>
                </a>
                <StyledLink href="/help" title="ヘルプ" id="help" testId="error-help-link">
                  ヘルプ
                </StyledLink>
              </>
            ) : null}
          </>
        ) : errorType === "exceeded-nlp-limit-x1" ? (
          <>
            {isMobile ? (
              <a style={{ width: "100%" }} target="_blank" href={props.oneLinkCheckoutOverNlpLimitCannotRetry1x}>
                <StyledButton fluid>今すぐアプリで確認する</StyledButton>
              </a>
            ) : null}
          </>
        ) : errorType === "hokatsu-rejected" ? (
          <StyledButton fluid onClick={onRedirect}>
            他の決済方法を選ぶ
          </StyledButton>
        ) : errorType === "kyc-rejected" ? (
          <StyledButton fluid onClick={onRedirect}>
            再度試す
          </StyledButton>
        ) : errorType === "timeout" ? (
          <StyledButton fluid onClick={onRedirect}>
            もう一度はじめから試す
          </StyledButton>
        ) : errorType === "outside-cic-hours" ? (
          <StyledButton fluid onClick={onRedirect}>
            他の決済方法を選ぶ
          </StyledButton>
        ) : errorType === "rejected" ? (
          <StyledButton fluid onClick={onRedirect}>
            他の決済方法を選ぶ
          </StyledButton>
        ) : errorType === "token-authentication-expired" ? (
          <>
            <StyledButton fluid onClick={onRedirect}>
              閉じる
            </StyledButton>
            <StyledLink href="/help" title="ヘルプ" id="help" testId="error-help-link">
              ヘルプ
            </StyledLink>
          </>
        ) : errorType === "forbidden" ? (
          <>
            <StyledButton fluid onClick={onRedirect}>
              他の支払い方法を選ぶ
            </StyledButton>
            <StyledLink href="/help" title="ヘルプ" id="help" testId="error-help-link">
              ヘルプ
            </StyledLink>
          </>
        ) : errorType === "payment-authentication-failed" ? (
          <>
            <StyledButton fluid onClick={onRedirect}>
              他の支払い方法を選ぶ
            </StyledButton>
            <StyledLink href="/help" title="ヘルプ" id="help" testId="error-help-link">
              ヘルプ
            </StyledLink>
          </>
        ) : errorType === "previously-rejected" ? (
          <>
            <StyledButton fluid onClick={onRedirect}>
              戻る
            </StyledButton>
            <StyledLink href="/help" title="ヘルプ" id="help" testId="error-help-link">
              ヘルプ
            </StyledLink>
          </>
        ) : errorType === "kyc-required" ? (
          <StyledLink href="/help" title="ヘルプ" id="help" testId="error-help-link">
            ヘルプ
          </StyledLink>
        ) : (
          <>
            <StyledButton fluid onClick={onRedirect}>
              他の支払い方法を選ぶ
            </StyledButton>
            <StyledLink href="/help" title="ヘルプ" id="help" testId="error-help-link">
              ヘルプ
            </StyledLink>
          </>
        )
      }
      id="modal"
      testId="error-modal"
    >
      {errorType === "overdue" && (
        <MainContent1>
          <TitleText>ペイディの決済を承認することができませんでした。</TitleText>
          <span>
            <span>お支払い待ちのご請求があるため、ペイディの決済を承認することができませんでした。</span>
            <br />
            <br />
            <span>下記のボタンからMyPaidyに進み、案内に従ってお支払い手続きを行ってください。</span>
          </span>
        </MainContent1>
      )}
      {errorType === "exceeded-plus-limit" && (
        <MainContent1>
          <Centered>
            <img src={"/assets/rejected.png"} width="184px" height="184px" />
          </Centered>
          <br />
          <TitleText>{exceedLimitContent[errorType].title}</TitleText>
          <div dangerouslySetInnerHTML={{ __html: exceedLimitContent[errorType].description }} />
          <br />
          {isMobile ? null : (
            <Download>
              <QRCodeContainer>
                <QRCode value={exceedLimitContent[errorType].qrcode} size={72} />
              </QRCodeContainer>
              <p style={{ fontWeight: 600, fontSize: 16, textAlign: "center" }}>
                QRコードを読み取って
                <br />
                アプリで確認する
              </p>
              <p style={{ fontSize: 12, marginTop: -10 }}>※エラーが表示される場合は下記からご利用ください</p>
              <div>
                <a href="https://apps.apple.com/jp/app/paidy/id1220373112" target="_blank" rel="noopener noreferrer">
                  <img src={"/assets/app-store-download.svg"} width={66} height={24} />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={"/assets/google-play-download.svg"} width={81} height={24} />
                </a>
              </div>
            </Download>
          )}
        </MainContent1>
      )}
      {errorType === "exceeded-plus-limit-retriable" && (
        <MainContent1>
          <TitleText>{exceedLimitContent[errorType].title}</TitleText>
          <div>
            ペイディのご利用可能枠を超過しているため、決済を承認することができませんでした。
            <br />
            <br />
            お手数ですが、下記のボタンをクリックして、店舗が提供する他の決済方法を選択してください。
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <StyledButton fluid onClick={onRedirect}>
              他の決済方法を選ぶ
            </StyledButton>
          </div>
          <div>
            なお、ペイディアプリから「すぐ払い」機能を利用して、コンビニでご請求金額をお支払いいただくことで、ご利用いただけるようになる場合がございます。詳細なご利用方法は、
            <a
              href="https://cs-support.paidy.com/support/solutions/articles/150000040753"
              target="_blank"
              rel="noopener noreferrer"
            >
              こちら
            </a>
            をご確認ください。
          </div>
          <br />
          {isMobile ? (
            <Download style={{ flexDirection: "row", justifyContent: "center", overflow: "hidden" }}>
              <img
                style={{ width: 450, marginBottom: -20 }}
                src={"/assets/exceeded-plus-limit-retriable-mobile.png"}
                alt="retriable"
              />
            </Download>
          ) : (
            <Download style={{ flexDirection: "row", alignItems: "flex-end" }}>
              <div style={{ flex: 1, overflow: "hidden", marginBottom: -20 }}>
                <img
                  style={{ width: 450, marginLeft: -140 }}
                  src={"/assets/exceeded-plus-limit-retriable-mobile.png"}
                  alt="retriable"
                />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <QRCodeContainer>
                  <QRCode value={exceedLimitContent[errorType].qrcode} size={64} />
                </QRCodeContainer>
                <p style={{ fontWeight: 600, fontSize: 14, textAlign: "center", marginTop: -5 }}>
                  QRコードを読み取って
                  <br />
                  アプリで確認する
                </p>
                <p style={{ fontSize: 10, marginTop: -10, textAlign: "center" }}>
                  ※エラーが表示される場合は下記からご利用ください
                </p>
                <div>
                  <a href="https://apps.apple.com/jp/app/paidy/id1220373112" target="_blank" rel="noopener noreferrer">
                    <img src={"/assets/app-store-download.svg"} width={66} height={24} />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={"/assets/google-play-download.svg"} width={81} height={24} />
                  </a>
                </div>
              </div>
            </Download>
          )}
        </MainContent1>
      )}
      {errorType === "merchant-requires-kyc" && (
        <MainContent1>
          <TitleText>大変申し訳ございませんが、ペイディの決済を承認することができませんでした。</TitleText>
          <div>
            ご利用の加盟店でペイディを利用するには、本人確認が必要です。お客様は本人確認が完了していないため、決済を承認することができませんでした。
            <br />
            <br />
            本人確認後はお客様のご利用可能枠が確認可能になり、その範囲内でぺイディをご利用いただけます。
            <br />
            <br />
            大変お手数をおかけいたしますが、ペイディアプリから本人確認後、もう一度お試しいただけますと幸いです。
          </div>
          <br />
          {isMobile ? (
            <Download style={{ flexDirection: "row", justifyContent: "center", overflow: "hidden" }}>
              <img style={{ width: 450, marginBottom: -20 }} src={"/assets/hoshino-kyc-required.png"} alt="retriable" />
            </Download>
          ) : (
            <Download style={{ flexDirection: "row", alignItems: "flex-end" }}>
              <div style={{ flex: 1, overflow: "hidden", marginBottom: -20 }}>
                <img
                  style={{ width: 420, marginLeft: -120 }}
                  src={"/assets/hoshino-kyc-required.png"}
                  alt="retriable"
                />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <QRCodeContainer>
                  <QRCode value={props.oneLinkMerchantRequiresKyc} size={64} />
                </QRCodeContainer>
                <p style={{ fontWeight: 600, fontSize: 14, textAlign: "center", marginTop: -5 }}>
                  QRコードを読み取って
                  <br />
                  アプリで確認する
                </p>
                <p style={{ fontSize: 10, marginTop: -10, textAlign: "center" }}>
                  ※エラーが表示される場合は下記からご利用ください
                </p>
                <div>
                  <a href="https://apps.apple.com/jp/app/paidy/id1220373112" target="_blank" rel="noopener noreferrer">
                    <img src={"/assets/app-store-download.svg"} width={66} height={24} />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={"/assets/google-play-download.svg"} width={81} height={24} />
                  </a>
                </div>
              </div>
            </Download>
          )}
        </MainContent1>
      )}
      {errorType === "exceeded-nlp-limit-retriable" && (
        <MainContent1>
          <TitleText>大変申し訳ございませんが、ペイディの決済を承認することができませんでした。</TitleText>
          {isMobile ? (
            <div>
              ペイディのご利用可能枠を超過しているため、決済を承認することができませんでした。
              <br />
              <br />
              お手数ですが、下記のボタンをタップして、店舗が提供する他の決済方法を選択してください。
              <br />
              <StyledButton fluid onClick={onRedirect}>
                他の決済方法を選ぶ
              </StyledButton>
              <br />
              なお、ペイディアプリから下記の機能を利用することで、ご利用いただけるようになる場合がございます。
              <br />
              <ul>
                <li>今月のご利用分を3・6・12回あと払いに変更する</li>
                <li>すぐ払い機能でご請求金額を支払う</li>
              </ul>
              詳細なご利用方法は、
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://cs-support.paidy.com/support/solutions/articles/150000040638-3%E3%83%BB6%E3%83%BB12%E5%9B%9E%E3%81%82%E3%81%A8%E6%89%95%E3%81%84%E3%81%AE%E5%88%A9%E7%94%A8%E6%96%B9%E6%B3%95%E3%81%8C%E7%9F%A5%E3%82%8A%E3%81%9F%E3%81%84"
              >
                こちら
              </a>
              をご確認ください。
            </div>
          ) : (
            <div>
              ペイディの今月のご利用可能枠を超過しているため、決済を承認することができませんでした。
              <br />
              <br />
              なお、ペイディアプリから下記の機能を利用することで、ご利用いただけるようになる場合がございます。
              <br />
              <ul>
                <li>今月のご利用分を3・6・12回あと払いに変更する</li>
                <li>すぐ払い機能でご請求金額を支払う</li>
              </ul>
              <br />
              詳細なご利用方法は、
              <a
                target="_blank"
                rel="noopener noreferrer"
                href="https://cs-support.paidy.com/support/solutions/articles/150000040638-3%E3%83%BB6%E3%83%BB12%E5%9B%9E%E3%81%82%E3%81%A8%E6%89%95%E3%81%84%E3%81%AE%E5%88%A9%E7%94%A8%E6%96%B9%E6%B3%95%E3%81%8C%E7%9F%A5%E3%82%8A%E3%81%9F%E3%81%84"
              >
                こちら
              </a>
              をご確認ください。
            </div>
          )}
          <br />
          {isMobile ? (
            <Download style={{ flexDirection: "row", justifyContent: "center", overflow: "hidden" }}>
              <img
                style={{ width: 200, marginBottom: -20 }}
                src={"/assets/over-limit-pay-now-eligible.png"}
                alt="retriable"
              />
            </Download>
          ) : (
            <Download style={{ flexDirection: "row", alignItems: "flex-end" }}>
              <div style={{ flex: 1, overflow: "hidden", marginBottom: -20 }}>
                <img style={{ width: 200 }} src={"/assets/over-limit-pay-now-eligible.png"} alt="retriable" />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <QRCodeContainer>
                  <QRCode value={props.oneLinkCheckoutExceededNlpLimitRetriable} size={64} />
                </QRCodeContainer>
                <p style={{ fontWeight: 600, fontSize: 14, textAlign: "center", marginTop: -5 }}>
                  QRコードを読み取って
                  <br />
                  アプリで確認する
                </p>
                <p style={{ fontSize: 10, marginTop: -10, textAlign: "center" }}>
                  ※エラーが表示される場合は下記からご利用ください
                </p>
                <div>
                  <a href="https://apps.apple.com/jp/app/paidy/id1220373112" target="_blank" rel="noopener noreferrer">
                    <img src={"/assets/app-store-download.svg"} width={66} height={24} />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={"/assets/google-play-download.svg"} width={81} height={24} />
                  </a>
                </div>
              </div>
            </Download>
          )}
        </MainContent1>
      )}
      {errorType === "exceeded-nlp-limit" && (
        <MainContent1>
          <Centered>
            <img src={"/assets/rejected.png"} width="184px" height="184px" />
          </Centered>
          <br />
          <TitleText>大変申し訳ございませんが、ペイディの決済を承認することができませんでした。</TitleText>
          {isMobile ? (
            <div>
              ペイディのご利用可能枠を超過しているため、決済が承認されませんでした。
              <br />
              <br />
              なお、今月のご利用可能枠は、ペイディアプリよりご確認いただけます。
            </div>
          ) : (
            <div>
              ペイディのご利用可能枠を超過しているため、決済を承認することができませんでした。
              <br />
              <br />
              なお、ご利用可能枠は、ペイディアプリよりご確認いただけます。
            </div>
          )}
          <br />
          {isMobile ? null : (
            <Download>
              <QRCodeContainer>
                <QRCode value={props.oneLinkCheckoutExceededNlpLimit} size={72} />
              </QRCodeContainer>
              <p style={{ fontWeight: 600, fontSize: 16, textAlign: "center" }}>
                QRコードを読み取って
                <br />
                アプリで確認する
              </p>
              <p style={{ fontSize: 12, marginTop: -10 }}>※エラーが表示される場合は下記からご利用ください</p>
              <div>
                <a href="https://apps.apple.com/jp/app/paidy/id1220373112" target="_blank" rel="noopener noreferrer">
                  <img src={"/assets/app-store-download.svg"} width={66} height={24} />
                </a>
                <a
                  href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img src={"/assets/google-play-download.svg"} width={81} height={24} />
                </a>
              </div>
            </Download>
          )}
        </MainContent1>
      )}
      {errorType === "exceeded-nlp-limit-retriable-x1" && (
        <MainContent1>
          <TitleText>ペイディの決済が承認されませんでした</TitleText>
          <div>
            ペイディのご利用可能額を超過しています。
            <br />
            <br />
            ペイディアプリから
            <a href={props.oneLinkCheckoutOverNlpLimitCanRetry1x} target="_blank" rel="noopener noreferrer">
              すぐ払い
            </a>
            機能でご請求金額をお支払いいただくと、ご利用いただけるようになる場合があります。
          </div>
          <br />
          {isMobile ? null : (
            <Download style={{ flexDirection: "row", alignItems: "flex-end" }}>
              <div style={{ flex: 1, overflow: "hidden", marginBottom: -20 }}>
                <img style={{ width: 200 }} src={"/assets/over-limit-pay-now-eligible.png"} alt="retriable" />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <QRCodeContainer>
                  <QRCode value={props.oneLinkCheckoutOverNlpLimitCanRetry1x} size={64} />
                </QRCodeContainer>
                <p style={{ fontWeight: 600, fontSize: 14, textAlign: "center", marginTop: -5 }}>
                  QRコードを読み取って
                  <br />
                  アプリで確認する
                </p>
                <p style={{ fontSize: 10, marginTop: -10, textAlign: "center" }}>
                  ※エラーが表示される場合は下記からご利用ください
                </p>
                <div>
                  <a href="https://apps.apple.com/jp/app/paidy/id1220373112" target="_blank" rel="noopener noreferrer">
                    <img src={"/assets/app-store-download.svg"} width={66} height={24} />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={"/assets/google-play-download.svg"} width={81} height={24} />
                  </a>
                </div>
              </div>
            </Download>
          )}
          <div style={{ fontSize: 12, color: "#999" }}>
            ※すぐ払いはコンビニ払いのみご利用可能です。
            <br />
            ※毎月のご請求のお支払い方法を「口座振替」に設定している場合、をご利用いただけません。お支払方法を「コンビニ払い・銀行振込」に変更後、コンビニですぐ払いをご利用ください。
          </div>
        </MainContent1>
      )}
      {errorType === "exceeded-nlp-limit-x1" && (
        <MainContent1>
          <TitleText>ペイディの決済が承認されませんでした</TitleText>
          <div>
            ペイディの今月のご利用可能枠を超過しています。
            <br />
            <br />
            今月のご利用可能枠は、ペイディアプリでご確認いただけます。
          </div>
          <br />
          {isMobile ? null : (
            <Download style={{ flexDirection: "row", alignItems: "flex-end" }}>
              <div style={{ flex: 1, overflow: "hidden", marginBottom: -20 }}>
                <img style={{ width: 200 }} src={"/assets/over-limit-pay-now-not-eligible.png"} alt="retriable" />
              </div>
              <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center" }}>
                <QRCodeContainer>
                  <QRCode value={props.oneLinkCheckoutOverNlpLimitCannotRetry1x} size={64} />
                </QRCodeContainer>
                <p style={{ fontWeight: 600, fontSize: 14, textAlign: "center", marginTop: -5 }}>
                  QRコードを読み取って
                  <br />
                  アプリで確認する
                </p>
                <p style={{ fontSize: 10, marginTop: -10, textAlign: "center" }}>
                  ※エラーが表示される場合は下記からご利用ください
                </p>
                <div>
                  <a href="https://apps.apple.com/jp/app/paidy/id1220373112" target="_blank" rel="noopener noreferrer">
                    <img src={"/assets/app-store-download.svg"} width={66} height={24} />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img src={"/assets/google-play-download.svg"} width={81} height={24} />
                  </a>
                </div>
              </div>
            </Download>
          )}
        </MainContent1>
      )}
      {errorType === "hokatsu-rejected" && (
        <MainContent1>
          <Centered>
            <img src={"/assets/rejected.png"} width="184px" height="184px" />
          </Centered>
          <br />
          <CenteredCol>
            <TitleText>本人確認の結果について</TitleText>
          </CenteredCol>
          <span>
            ペイディでは、すべてのお客様に安心・安全にご利用いただける健全なサービスの運営を目指しており、その取り組みのひとつとして、審査を行っております。今回、お客様にお手続きいただいた内容をもとに検討を行いましたが、誠に残念ながら、今回のご利用は見送りとさせていただくこととなりました。お客様のご期待にそえず、誠に申し訳ございません。
            <br />
            <br />
            お手数ですが、店舗が提供する他の決済方法をご利用ください。
          </span>
        </MainContent1>
      )}
      {errorType === "kyc-rejected" && (
        <MainContent1>
          <Centered>
            <img src={"/assets/ekyc-rejected.svg"} width="184px" height="184px" />
          </Centered>
          <br />
          <CenteredCol>
            <TitleText>
              再度本人確認の手続きを
              <br />
              お願いします
            </TitleText>
          </CenteredCol>
          <span>
            本人確認の手続きを行っていただき、誠にありがとうございます。大変申し訳ございませんが、下記のような理由により、本人確認ができませんでした。
          </span>
          <br />
          <ul>
            <li>身分証明書の情報が入力情報と一致しない</li>
            <li>身分証明書の厚みが不自然</li>
            <li>身分証明証の情報が読み取れない など</li>
          </ul>
          <span>お手数ではございますが、再度お試しください。</span>
        </MainContent1>
      )}
      {errorType === "timeout" && (
        <MainContent1>
          <Centered>
            <img src={"/assets/timeout.png"} width="184px" height="184px" />
          </Centered>
          <br />
          <CenteredCol>
            <TitleText>セッションの有効期限が切れました</TitleText>
            <span>お手数おかけしますが、</span>
            <span>もう一度はじめからお試しください。</span>
          </CenteredCol>
        </MainContent1>
      )}
      {errorType === "outside-cic-hours" && (
        <MainContent1>
          <Centered>
            <img src={"/assets/timeout.png"} width="184px" height="184px" />
          </Centered>
          <br />
          <CenteredCol>
            <TitleText>ペイディの決済が完了できません</TitleText>
          </CenteredCol>
          <span>
            大変申し訳ございませんが、翌朝8:00までペイディの本人確認の結果の確認ができないため、ペイディの決済が完了できません
          </span>
          <br />
          <br />
          <span>
            翌朝8:00以降に本人確認の結果を確認後、改めてペイディでの購入をお試しいただくか、他の決済方法を選択してください。
          </span>
        </MainContent1>
      )}
      {errorType === "rejected" && (
        <MainContent1>
          <Centered>
            <img src={"/assets/rejected.png"} width="184px" height="184px" />
          </Centered>
          <br />
          <TitleText>大変申し訳ございませんが、ペイディの決済を承認することができませんでした。</TitleText>
          <span>
            <span>お手数ですが、店舗が提供する他の決済方法を選択してください。</span>
            <br />
            <br />
            <span>
              ペイディでは、すべてのお客様に安心・安全にご利用いただける健全なサービスの運営を目指しており、その取り組みのひとつとして、ご注文ごとにご利用内容の確認を行っております。そのため、店舗や商品などのご注文内容によって、決済が承認されない場合があります。
            </span>
            <br />
            <br />
            <span>
              なお、詳細な審査基準に関しましてはお答えすることができません。ご迷惑をおかけして誠に申し訳ございませんが、何卒ご理解いただけますと幸いです。
            </span>
          </span>
        </MainContent1>
      )}
      {errorType === "token-authentication-expired" && (
        <MainContent1>
          <TitleText>正しい認証コードを確認できませんでした</TitleText>
          <span>
            <span>ご入力いただいた認証コードに誤りがあるか、一定の時間が経過しています。</span>
            <br />
            <br />
            <span>お手数ですが、ペイディの画面を閉じて、もう一度お試しください。</span>
          </span>
        </MainContent1>
      )}
      {errorType === "forbidden" && (
        <MainContent1>
          <TitleText>今回のお支払いは承認されませんでした</TitleText>
          <span>
          ペイディご利用対象外の内容が含まれています。恐れ入りますが、他のお支払い方法をお選び下さい。 今回承認とならなかった理由の詳細はお伝えいたしかねますが、ご了承ください。
          </span>
        </MainContent1>
      )}
      {errorType === "payment-authentication-failed" && (
        <MainContent1>
          <TitleText>今回のお支払いは承認されませんでした</TitleText>
          <span>
            <span>恐れ入りますが、他の支払い方法を選びください。</span>
            <br />
            <br />
            <span>今回、承認とならなかった理由の詳細はお伝えいたしかねますので、ご了承ください。</span>
          </span>
        </MainContent1>
      )}
      {errorType === "previously-rejected" && (
        <MainContent1>
          <TitleText>ペイディの決済が承認されませんでした</TitleText>
          <span>
            <span>
              お手数ですが、店舗が提供する他の決済方法を選択してください。
            </span>
            <br />
            <br />
            <span>
              なお、詳細な審査基準に関しましてはお答えすることができません。ご迷惑をおかけして誠に申し訳ございませんが、何卒ご理解いただけますと幸いです。
            </span>
          </span>
        </MainContent1>
      )}
      {errorType === 'kyc-required' && (
        <MainContent1>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 16 }}>
            <img src={"/assets/icon-info-colored.svg"} width="64px" height="64px" />
            <TitleText style={{ marginBottom: '1rem' }}>本人確認が必要です</TitleText>
            <span>ペイディアプリをダウンロードして、</span>
            <span>本人確認を行ってください。</span>
          </div>
          <div style={{ marginBottom: 16, padding: 16, border: '1px solid #a3a3a3', borderRadius: 16, display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
            <a
              href="https://apps.apple.com/jp/app/paidy/id1220373112"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <img
                src={'/assets/badge-apple-app-store.png'}
                alt="Apple App Store badge"
                width={168}
                height={48}
              />
            </a>
            <a
              href="https://play.google.com/store/apps/details?id=com.paidy.paidy&amp;hl=ja"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              <img
                src={'/assets/badge-google-play-store.png'}
                alt="Apple App Store badge"
                width={168}
                height={48}
              />
            </a>
          </div>
          <span>
            <b>最短5分、３ステップで完了。</b>
            <br /><br />
            <span>①アプリの「アカウント」から本人確認を行ってください。運転免許証またはマイナンバーカードがご利用いただけます。</span>
            <br />
            <br />
            <span>
            ②通常数分以内に、結果をメールでお知らせ。アプリでご利用可能額をご確認ください。
            </span>
            <br />
            <br />
            <span>
            ③再度購入をお試しください。
            </span>
          </span>
        </MainContent1>
      )}
      {errorType === "internal" && (
        <>
          <MainContent1>
            <TitleText>一時的にペイディを利用できません</TitleText>
            <span>
              <span>誠に申し訳ございませんが、時間をおいてから再度お試しください。</span>
              <br />
              <br />
              <span>ご不明な点につきましては、ペイディカスタマーサポートまでお問い合わせください。</span>
              <br />
              <br />
              <b>ペイディカスタマーサポート</b>
              <br />
              <Link href="tel:0120971918">0120-971-918</Link>
              <br />
              <br />
            </span>
          </MainContent1>
        </>
      )}
      {['generic', 'request'].includes(errorType) && (
        <>
          <MainContent1>
            <TitleText>現在こちらのショップでペイディが利用できません</TitleText>
            <span>
              <span>誠に申し訳ございませんが、時間をおいてから再度お試しください。</span>
              <br />
              <br />
              <b>解決しない場合</b>
              <br />
              <br />
              <span>お手数をおかけいたしますが、ご利用になっているオンラインショップへお問い合わせいただくか、ペイディカスタマーサポートまでお問い合わせください。</span>
              <br />
              <br />
              <b>ペイディカスタマーサポート</b>
              <br />
              <Link href="tel:0120971918">0120-971-918</Link>
              <br />
              <br />
            </span>
          </MainContent1>
        </>
      )}
    </StyledModal>
  );
};

export default Error;
